import React from 'react';
import { Box, Typography, LinearProgress, Chip, Stack, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface SeoAuditScoreCardProps {
  score: number;
  checks: { label: string; passed: boolean; tip: string }[];
  compact?: boolean;
}

export default function SeoAuditScoreCard({ score, checks, compact = false }: SeoAuditScoreCardProps) {
  const getScoreColor = (val: number) => {
    if (val >= 90) return '#2e7d32'; // green
    if (val >= 70) return '#ed6c02'; // orange
    return '#d32f2f'; // red
  };

  const getScoreBadge = (val: number) => {
    if (val >= 90) return { text: 'Fields mostly complete', color: 'success' as const };
    if (val >= 70) return { text: 'Some fields missing', color: 'warning' as const };
    return { text: 'Many fields missing', color: 'error' as const };
  };

  const badge = getScoreBadge(score);

  if (compact) {
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{ width: 45, height: 45, borderRadius: '50%', border: `3px solid ${getScoreColor(score)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, color: getScoreColor(score) }}>
          {score}%
        </Box>
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 600, color: getScoreColor(score), display: 'block' }}>
            {badge.text}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {checks.filter(c => c.passed).length}/{checks.length} audits passed
          </Typography>
        </Box>
      </Stack>
    );
  }

  return (
    <Box sx={{ p: 2.5, bgcolor: '#ffffff', borderRadius: 2, border: '1px solid #e8e8e8', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a1a1a', letterSpacing: 0.3 }}>
            CONTENT FIELD CHECKLIST
          </Typography>
          <Typography variant="caption" color="text.secondary">
            This checks editor fields, not rankings, indexing, or live page quality.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip label={badge.text} color={badge.color} size="small" sx={{ fontWeight: 600, fontSize: 11 }} />
          <Typography variant="h5" sx={{ fontWeight: 800, color: getScoreColor(score), minWidth: 50, textAlign: 'right' }}>
            {score}%
          </Typography>
        </Stack>
      </Stack>

      <LinearProgress
        variant="determinate"
        value={score}
        sx={{
          height: 8,
          borderRadius: 4,
          bgcolor: 'rgba(0,0,0,0.06)',
          mb: 2,
          '& .MuiLinearProgress-bar': {
            bgcolor: getScoreColor(score),
            borderRadius: 4,
          },
        }}
      />

      <Accordion disableGutters elevation={0} sx={{ border: '1px solid #f0f0f0', borderRadius: 1.5, '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />} sx={{ minHeight: 38, py: 0.5, bgcolor: '#fafaf9' }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#555' }}>
            View {checks.length} Automated Audit Criteria ({checks.filter(c => c.passed).length} Passed)
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 1.5, bgcolor: '#fafaf9' }}>
          <Stack spacing={1}>
            {checks.map((item, idx) => (
              <Stack key={idx} direction="row" spacing={1.5} alignItems="flex-start">
                {item.passed ? (
                  <CheckCircleOutlineIcon sx={{ fontSize: 17, color: '#2e7d32', mt: 0.2, flexShrink: 0 }} />
                ) : (
                  <ErrorOutlineIcon sx={{ fontSize: 17, color: '#d32f2f', mt: 0.2, flexShrink: 0 }} />
                )}
                <Box>
                  <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 600, color: item.passed ? '#2e7d32' : '#d32f2f' }}>
                    {item.label}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: 11, color: 'text.secondary', display: 'block' }}>
                    {item.tip}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
